import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import axios from 'axios';

interface UserPreferences {
  defaultPipelineView?: 'k' | 'l';
  defaultCalendarView?: 'Month' | 'Week' | 'Day' | 'List';
}

interface User {
  id: string;
  name: string;
  email: string;
  initials: string;
  colour: string;
  role: 'admin' | 'standard';
  status: string;
  preferences?: UserPreferences;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Public form routes — these never need an authenticated user, so we skip
// the /api/auth/me call entirely. Saves a roundtrip + a 401 in the console
// when the form is iframed on the marketing site.
const PUBLIC_ONLY_PATHS = ['/book-demo', '/spycloud', '/platform-strategy', '/pre-recorded-demo', '/contact-us'];
const isPublicOnlyRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return PUBLIC_ONLY_PATHS.some((p) => path === p || path.startsWith(p + '/'));
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(!isPublicOnlyRoute());

  useEffect(() => {
    if (!isPublicOnlyRoute()) refreshUser();
  }, []);

  async function refreshUser() {
    try {
      const { data } = await axios.get('/api/auth/me', { withCredentials: true });
      setUser(data.data);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    await axios.post('/api/auth/logout', {}, { withCredentials: true });
    setUser(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isLoading,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
